import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { fetchImage } from "../../api/newsService";
import imageLibraryBackground from "../../assets/hinhanh_bg.jpg";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import MotionReveal from "../motion/MotionReveal";

const ImageLibrarySection = () => {
  const [postImage, setPostImage] = useState([]);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(true);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);
  const { ref: sectionRef, inView: sectionInView } = useInView({
    triggerOnce: true,
    threshold: 0.12,
  });

  const getImages = async () => {
    const res = await fetchImage();
    if (res?.data?.data?.lstImage) {
      setPostImage(res.data.data.lstImage);
    }
  };

  useEffect(() => {
    getImages();
  }, []);

  const updateNavState = useCallback((swiper) => {
    if (!swiper) return;

    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const swiper = swiperRef.current;
      if (!swiper) return;

      swiper.update();
      updateNavState(swiper);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [postImage.length, updateNavState]);

  return (
    <section
      className="relative w-full bg-cover bg-center bg-no-repeat py-10 md:py-14"
      style={{ backgroundImage: `url(${imageLibraryBackground})` }}
    >
      <div
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,18,56,0.4)_0%,rgba(4,18,56,0.3)_40%,rgba(4,18,56,0.24)_100%)] md:hidden"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 hidden bg-[linear-gradient(180deg,rgba(4,18,56,0.18)_0%,rgba(4,18,56,0.12)_100%)] md:block"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 2xl:max-w-[1440px]">
        <MotionReveal direction="up" delay={0.2}>
          <div className="mb-6 md:mb-8 lg:mb-10 flex items-center justify-between gap-4">
            <div className="mx-auto text-center md:mx-0 md:text-left">
              <div className="relative inline-flex flex-col items-center md:items-start">
                <div className="relative flex items-center">
                  <h2 className="section-tech-title section-tech-title--light text-[24px] md:text-[32px] lg:text-[44px] font-bold drop-shadow-[0_2px_10px_rgba(0,0,0,0.3)] md:drop-shadow-none">
                    Thư viện hình ảnh
                  </h2>
                </div>
              </div>
              <p className="mt-3 max-w-[760px] text-base font-base leading-8 !text-white/95 md:text-[19px]">
                Ghi lại những khoảnh khắc, sự kiện và hình ảnh tiêu biểu qua các giai đoạn phát triển.
              </p>
            </div>

            <div className="hidden items-center gap-2 md:flex">
              <button
                type="button"
                ref={prevRef}
                disabled={isBeginning}
                aria-label="Ảnh trước"
                className="image-library-nav-button flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/80 backdrop-blur-sm transition hover:bg-white/20 hover:text-white"
              >
                <ChevronLeft className="image-library-nav-icon h-5 w-5" />
              </button>
              <button
                type="button"
                ref={nextRef}
                disabled={isEnd}
                aria-label="Ảnh tiếp theo"
                className="image-library-nav-button flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/80 backdrop-blur-sm transition hover:bg-white/20 hover:text-white"
              >
                <ChevronRight className="image-library-nav-icon h-5 w-5" />
              </button>
            </div>
          </div>
        </MotionReveal>

        <MotionReveal direction="up" delay={0.35} amount={0.1}>
          <div ref={sectionRef}>
            <Swiper
              modules={[Navigation, Pagination]}
              className="image-library-swiper"
              centeredSlides={false}
              spaceBetween={18}
              slidesPerView={4}
              slidesPerGroup={1}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                updateNavState(swiper);
              }}
              onAfterInit={updateNavState}
              onSlideChange={updateNavState}
              onReachBeginning={updateNavState}
              onReachEnd={updateNavState}
              onFromEdge={updateNavState}
              onBreakpoint={updateNavState}
              onResize={updateNavState}
              onUpdate={updateNavState}
              onBeforeInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }}
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              pagination={{ clickable: true }}
              breakpoints={{
                0: { slidesPerView: 1.4, slidesPerGroup: 1, spaceBetween: 18, centeredSlides: postImage.length > 1 },
                480: { slidesPerView: 1.8, slidesPerGroup: 1, spaceBetween: 14, centeredSlides: postImage.length > 1 },
                640: { slidesPerView: 2.3, slidesPerGroup: 1, spaceBetween: 16, centeredSlides: postImage.length > 1 },
                768: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 20, centeredSlides: false },
                1024: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 24, centeredSlides: false },
              }}
            >
              {postImage?.slice(0, 8).map((item, index) => (
                <SwiperSlide key={item.id || index} className="!h-auto">
                  <div
                    className={`h-full transform-gpu transition-all duration-700 ease-out ${
                      sectionInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                    }`}
                    style={{ transitionDelay: `${120 + index * 80}ms` }}
                  >
                    <Link
                      to={`/thu-vien-hinh-anh/${item.id}`}
                      className="group block h-full overflow-hidden rounded-[20px] border border-transparent bg-white/10 p-3 transition-all duration-300 hover:border-white/70 hover:bg-white/10"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden rounded-[12px] bg-slate-200">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06] hover:brightness-115"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

                        <div className="absolute inset-x-0 bottom-0 p-4">
                          <div className="rounded-2xl bg-white/12 p-3 backdrop-blur-md">
                            <h3 className="line-clamp-2 text-sm font-semibold leading-6 text-white">
                              {item.title}
                            </h3>
                            <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-white/85">
                              Xem chi tiết
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </MotionReveal>

        <MotionReveal direction="up" delay={0.5} amount={0.1}>
          <div className="mt-4 flex items-center justify-center gap-2 md:hidden">
            <button
              type="button"
              ref={prevRef}
              disabled={isBeginning}
              aria-label="Ảnh trước"
              onClick={() => swiperRef.current?.slidePrev()}
              className="image-library-nav-button group flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/80 backdrop-blur-sm transition-all duration-200 hover:bg-white/20 hover:text-white hover:shadow-lg active:scale-90 active:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <ChevronLeft className="image-library-nav-icon h-5 w-5 transition-transform duration-200 group-hover:-translate-x-0.5" />
            </button>

            <button
              type="button"
              ref={nextRef}
              disabled={isEnd}
              aria-label="Ảnh tiếp theo"
              onClick={() => swiperRef.current?.slideNext()}
              className="image-library-nav-button group flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/80 backdrop-blur-sm transition-all duration-200 hover:bg-white/20 hover:text-white hover:shadow-lg active:scale-90 active:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <ChevronRight className="image-library-nav-icon h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </div>
        </MotionReveal>
      </div>
    </section>
  );
};

export default ImageLibrarySection;
